// src/components/support/CreateTicketModal.jsx
import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Loader2, Tag, AlertTriangle, XCircle, Plus } from 'lucide-react';
import ticketService from '../../services/ticketService';
import useAuth from '../../hooks/useAuth';
import { saveTicketToSheet } from '../../services/googleSheetsService';

const CATEGORIES = {
  DELIVERY_ISSUE: {
    label: 'Delivery Issue',
    subcategories: {
      delay: 'Delay',
      failed_attempt: 'Failed Attempt',
      wrong_address: 'Wrong Address',
      customer_unavailable: 'Customer Unavailable',
    },
  },
  PICKUP_ISSUE: {
    label: 'Pickup Issue',
    subcategories: {
      delay: 'Delay',
      merchant_unavailable: 'Merchant Unavailable',
      incorrect_details: 'Incorrect Details',
    },
  },
  DAMAGE: {
    label: 'Damage',
    subcategories: {
      packaging: 'Packaging',
      product: 'Product',
      partial: 'Partial',
    },
  },
  LOST: {
    label: 'Lost',
    subcategories: {
      in_transit: 'In Transit',
      at_facility: 'At Facility',
      untraced: 'Untraced',
    },
  },
  RTO: {
    label: 'RTO (Return to Origin)',
    subcategories: {
      customer_refused: 'Customer Refused',
      undeliverable: 'Undeliverable',
      incorrect_address: 'Incorrect Address',
    },
  },
  COD_ISSUE: {
    label: 'COD Issue',
    subcategories: {
      amount_mismatch: 'Amount Mismatch',
      payment_not_collected: 'Payment Not Collected',
      reconciliation: 'Reconciliation',
    },
  },
  OTHER: {
    label: 'Other',
    subcategories: {},
  },
};

const SEVERITY_OPTIONS = [
  { value: 'LOW', label: 'Low', color: 'text-gray-600' },
  { value: 'MEDIUM', label: 'Medium', color: 'text-blue-600' },
  { value: 'HIGH', label: 'High', color: 'text-orange-600' },
  { value: 'CRITICAL', label: 'Critical', color: 'text-red-600' },
];

const PRIORITY_OPTIONS = [
  { value: 'P0', label: 'P0 - Critical', color: 'text-red-600' },
  { value: 'P1', label: 'P1 - High', color: 'text-orange-600' },
  { value: 'P2', label: 'P2 - Medium', color: 'text-yellow-600' },
  { value: 'P3', label: 'P3 - Low', color: 'text-blue-600' },
  { value: 'P4', label: 'P4 - Very Low', color: 'text-gray-600' },
];

const CreateTicketModal = ({ isOpen, onClose, onSuccess, orderContext = null }) => {
  const { getMerchantId, getParentClientId } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    description_format: 'text',
    category: '',
    subcategory: '',
    tags: [],
    suggested_severity: 'MEDIUM',
    suggested_priority: 'P3',
    customFields: {
      expected_delivery_date: '',
      customer_name: '',
      customer_phone: '',
      customer_email: '',
    },
  });

  const merchantId = getMerchantId();
  const parentClientId = getParentClientId();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: '',
        description: '',
        description_format: 'text',
        category: '',
        subcategory: '',
        tags: [],
        suggested_severity: 'MEDIUM',
        suggested_priority: 'P3',
        customFields: {
          expected_delivery_date: '',
          customer_name: '',
          customer_phone: '',
          customer_email: '',
        },
      });
      setTagInput('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Build custom fields object, filtering out empty values
      const customFields = {
        ...(orderContext && {
          order_reference_number: orderContext.orderReferenceNumber,
          ...(orderContext.awbNumber && { awb_number: orderContext.awbNumber }),
        }),
        ...Object.fromEntries(
          Object.entries(formData.customFields).filter(([_, value]) => value && value.trim() !== '')
        ),
      };

      const ticketPayload = {
        tenant_id: 'wayuvega_b2c',
        requester: {
          actor_type: 'merchant',
          actor_id: merchantId,
          ...(parentClientId && { name: parentClientId }),
        },
        channel: 'MERCHANT_PORTAL',
        title: formData.title.trim(),
        description: formData.description.trim(),
        description_format: formData.description_format,
        category: formData.category,
        ...(formData.subcategory && { subcategory: formData.subcategory }),
        ...(formData.tags.length > 0 && { tags: formData.tags }),
        suggested_severity: formData.suggested_severity,
        suggested_priority: formData.suggested_priority,
        ...(Object.keys(customFields).length > 0 && { custom_fields: customFields }),
        ...(orderContext && {
          idempotency_key: `${merchantId}:${orderContext.orderReferenceNumber}`,
          external_refs: [
            {
              ref_type: 'order_reference_number',
              ref_id: orderContext.orderReferenceNumber,
            },
            ...(orderContext.awbNumber
              ? [
                  {
                    ref_type: 'shipment_order_id',
                    ref_id: orderContext.awbNumber,
                  },
                ]
              : []),
          ],
        }),
      };

      const createdTicket = await ticketService.createTicket(ticketPayload);
      
      // Save to Google Sheets (non-critical, don't fail ticket creation if this fails)
      try {
        await saveTicketToSheet(createdTicket, 'create');
        console.log('✅ Ticket data saved to Google Sheets');
      } catch (sheetError) {
        console.warn('⚠️ Failed to save ticket to Google Sheets (non-critical):', sheetError);
        // Don't set error state - this is optional tracking
      }
      
      if (onSuccess) {
        onSuccess(createdTicket);
      }
      onClose();
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to create ticket';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'category' && { subcategory: '' }), // Reset subcategory when category changes
    }));
  };

  const handleCustomFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      customFields: {
        ...prev.customFields,
        [field]: value,
      },
    }));
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !formData.tags.includes(trimmed)) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, trimmed],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const selectedCategory = formData.category ? CATEGORIES[formData.category] : null;
  const hasSubcategories = selectedCategory && Object.keys(selectedCategory.subcategories).length > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[95vh] overflow-hidden flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">Create Support Ticket</h2>
            {orderContext && (
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 px-3 py-1 bg-indigo-100 rounded-lg">
                  <span className="text-xs font-medium text-indigo-700">Order:</span>
                  <span className="text-sm font-semibold text-indigo-900">
                    {orderContext.orderReferenceNumber}
                  </span>
                </div>
                {orderContext.awbNumber && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-purple-100 rounded-lg">
                    <span className="text-xs font-medium text-purple-700">AWB:</span>
                    <span className="text-sm font-semibold text-purple-900">
                      {orderContext.awbNumber}
                    </span>
                  </div>
                )}
                <span className="text-xs text-gray-500 italic">
                  Order details will be automatically linked to this ticket
                </span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/80 rounded-lg transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                id="title"
                type="text"
                required
                maxLength={200}
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Brief summary of the issue"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
              <p className="text-xs text-gray-500 mt-1">{formData.title.length}/200 characters</p>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                required
                rows={6}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Provide detailed information about the issue..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
              />
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-semibold text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                id="category"
                required
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
              >
                <option value="">Select a category</option>
                {Object.entries(CATEGORIES).map(([key, { label }]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Subcategory */}
            {hasSubcategories && (
              <div>
                <label htmlFor="subcategory" className="block text-sm font-semibold text-gray-700 mb-2">
                  Subcategory
                </label>
                <select
                  id="subcategory"
                  value={formData.subcategory}
                  onChange={(e) => handleChange('subcategory', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
                >
                  <option value="">Select a subcategory (optional)</option>
                  {Object.entries(selectedCategory.subcategories).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Priority and Severity */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="priority" className="block text-sm font-semibold text-gray-700 mb-2">
                  Priority
                  <span className="text-xs font-normal text-gray-500 ml-2">(Suggested)</span>
                </label>
                <select
                  id="priority"
                  value={formData.suggested_priority}
                  onChange={(e) => handleChange('suggested_priority', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
                >
                  {PRIORITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Higher priority tickets get faster response times
                </p>
              </div>

              <div>
                <label htmlFor="severity" className="block text-sm font-semibold text-gray-700 mb-2">
                  Severity
                  <span className="text-xs font-normal text-gray-500 ml-2">(Suggested)</span>
                </label>
                <select
                  id="severity"
                  value={formData.suggested_severity}
                  onChange={(e) => handleChange('suggested_severity', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all bg-white"
                >
                  {SEVERITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Indicates the impact level of the issue
                </p>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label htmlFor="tags" className="block text-sm font-semibold text-gray-700 mb-2">
                Tags
                <span className="text-xs font-normal text-gray-500 ml-2">(Optional)</span>
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  id="tags"
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagInputKeyDown}
                  placeholder="Add tags to categorize (e.g., urgent, customer_complaint)"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium"
                    >
                      <Tag className="w-3 h-3" />
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-indigo-900"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Press Enter or click Add to add a tag
              </p>
            </div>

            {/* Additional Information Section */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-indigo-600" />
                Additional Information
                <span className="text-sm font-normal text-gray-500">(Optional)</span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Expected Delivery Date */}
                <div>
                  <label
                    htmlFor="expected_delivery_date"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Expected Delivery Date
                  </label>
                  <input
                    id="expected_delivery_date"
                    type="date"
                    value={formData.customFields.expected_delivery_date}
                    onChange={(e) => handleCustomFieldChange('expected_delivery_date', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Customer Name */}
                <div>
                  <label
                    htmlFor="customer_name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Customer Name
                  </label>
                  <input
                    id="customer_name"
                    type="text"
                    value={formData.customFields.customer_name}
                    onChange={(e) => handleCustomFieldChange('customer_name', e.target.value)}
                    placeholder="Customer's name"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Customer Phone */}
                <div>
                  <label
                    htmlFor="customer_phone"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Customer Phone
                  </label>
                  <input
                    id="customer_phone"
                    type="tel"
                    value={formData.customFields.customer_phone}
                    onChange={(e) => handleCustomFieldChange('customer_phone', e.target.value)}
                    placeholder="+91-XXXXXXXXXX"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Customer Email */}
                <div>
                  <label
                    htmlFor="customer_email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Customer Email
                  </label>
                  <input
                    id="customer_email"
                    type="email"
                    value={formData.customFields.customer_email}
                    onChange={(e) => handleCustomFieldChange('customer_email', e.target.value)}
                    placeholder="customer@example.com"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || !formData.title || !formData.description || !formData.category}
            className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Ticket'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTicketModal;

